import { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { ActionHintIcon } from '#/modules/commonComponents/hint';
import TabbedDetailCard from '#/modules/commonComponents/layout/TabbedDetailCard';
import { useCan } from '#/modules/infra/auth/useCan';
import {
  WORK_ORDER_AMENDMENT_STATUS,
  canCreateWorkOrderAmendment,
  findOpenWorkOrderAmendment,
  hasOpenWorkOrderAmendment,
} from '#/modules/momMgmt/workOrder/helper/amendmentStatus';
import {
  buildPurchaseOrderCreateUrlFromRequirements,
} from '#/modules/momMgmt/workOrder/helper/buildPurchaseOrderCreateUrlFromRequirements';
import {
  hasRequirementShortage,
  resolveRequirementRowKey,
} from '#/modules/momMgmt/workOrder/helper/materialRequirementPrefix';
import {
  WORK_ORDER_STATUS,
  canOperateWorkOrderStockDocs,
} from '#/modules/momMgmt/workOrder/helper/workOrderStatus';

import WorkOrderAmendmentsPanel from './WorkOrderAmendmentsPanel';
import WorkOrderBomSelectionsTable from './WorkOrderBomSelectionsTable';
import WorkOrderExpensesTable from './WorkOrderExpensesTable';
import WorkOrderRepairsPanel from './WorkOrderRepairsPanel';
import WorkOrderSnLabelPanel from './WorkOrderSnLabelPanel';
import WorkOrderMaterialRequirementsTable from './WorkOrderMaterialRequirementsTable';
import WorkOrderRelatedDocsTable from './WorkOrderRelatedDocsTable';

const OPEN_AMENDMENT_HINT = '已有草稿／待核准異動單，請先處理完成後再新建。';

const DRAFT_TAB_TIP = '草稿／審核中尚無出庫歷史、入庫歷史與異動單。';

const PURCHASE_REQUIREMENT_HINT = '需要先勾選物料，才能進行採購（採購量帶入短缺量）';

const STOCK_DOC_DISABLED_HINT = '僅生產中的工單可新增';

const AMENDMENT_NOT_PROCESSING_HINT = '僅生產中的工單可新建異動單';

const WORK_ORDER_DETAIL_TABS = [
  'materialRequirements',
  'bomSelections',
  'expenses',
  'snLabels',
  'repairs',
  'stockIssues',
  'stockReceipts',
  'amendments',
];

const FULFILLMENT_DETAIL_TABS = ['stockIssues', 'stockReceipts', 'amendments'];

const DEFAULT_WORK_ORDER_DETAIL_TAB = 'materialRequirements';

function resolveWorkOrderDetailTab(tab, fulfillmentTabsDisabled) {
  if (!WORK_ORDER_DETAIL_TABS.includes(tab)) {
    return DEFAULT_WORK_ORDER_DETAIL_TAB;
  }
  if (fulfillmentTabsDisabled && FULFILLMENT_DETAIL_TABS.includes(tab)) {
    return DEFAULT_WORK_ORDER_DETAIL_TAB;
  }
  return tab;
}

function DraftTabTip({ label, className }) {
  return (
    <ActionHintIcon
      tip={DRAFT_TAB_TIP}
      ariaLabel={`${label}說明`}
      className={className}
    />
  );
}

DraftTabTip.propTypes = {
  label: PropTypes.string.isRequired,
  className: PropTypes.string,
};

function renderStockDocAction(workOrderId, to, label, enabled) {
  if (enabled && workOrderId) {
    return (
      <Button asChild size="sm">
        <Link to={to}>{label}</Link>
      </Button>
    );
  }

  return (
    <Button type="button" size="sm" disabled>
      <span className="inline-flex items-center gap-0.5">
        {label}
        <ActionHintIcon
          tip={STOCK_DOC_DISABLED_HINT}
          ariaLabel={`${label}說明`}
        />
      </span>
    </Button>
  );
}

function buildAmendmentPaneHint({
  workOrderId,
  showOpenHint,
  openDraft,
  canEditDraft,
}) {
  if (!showOpenHint && !(openDraft && canEditDraft)) {
    return null;
  }

  return (
    <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
      {showOpenHint ? <span>{OPEN_AMENDMENT_HINT}</span> : null}
      {openDraft && canEditDraft && workOrderId ? (
        <Link
          to={ROUTES.workOrderAmendmentEdit(workOrderId, openDraft.id)}
          className="text-sm font-medium text-primary-600 hover:underline"
        >
          編輯異動單
        </Link>
      ) : null}
    </span>
  );
}

const WorkOrderDetailSection = ({ detail, onRefetch }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedRequirementKeys, setSelectedRequirementKeys] = useState(
    () => new Set(),
  );
  const canCreateWorkOrder = useCan(PERM.WORK_ORDER_CREATE);
  const canCreateAmendmentPerm = useCan(PERM.WORK_ORDER_AMENDMENT_CREATE);
  const canUpdateAmendment = useCan(PERM.WORK_ORDER_AMENDMENT_UPDATE);

  const workOrderId = detail?.id;
  const materialRequirements = useMemo(
    () => detail?.mtrRequirements ?? detail?.materialRequirements ?? [],
    [detail?.mtrRequirements, detail?.materialRequirements],
  );
  const fulfillmentTabsDisabled = detail?.status === WORK_ORDER_STATUS.DRAFT
    || detail?.status === WORK_ORDER_STATUS.SUBMITTED;
  const requestedTab = searchParams.get('tab');
  const activeTab = resolveWorkOrderDetailTab(
    requestedTab,
    fulfillmentTabsDisabled,
  );

  useEffect(() => {
    if (!requestedTab || requestedTab === activeTab) {
      return;
    }
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('tab');
      return next;
    }, { replace: true });
  }, [activeTab, requestedTab, setSearchParams]);

  const handleDetailTabChange = useCallback((nextTab) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (nextTab === DEFAULT_WORK_ORDER_DETAIL_TAB) {
        next.delete('tab');
      } else {
        next.set('tab', nextTab);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const canOperateStockDocs = canOperateWorkOrderStockDocs(detail?.status);
  const canCreateAmendment = canCreateAmendmentPerm
    && canCreateWorkOrderAmendment(detail);
  const openAmendment = findOpenWorkOrderAmendment(detail?.amendments);
  const showOpenHint = detail?.status === WORK_ORDER_STATUS.PROCESSING
    && hasOpenWorkOrderAmendment(detail?.amendments);
  const openDraft = openAmendment?.status === WORK_ORDER_AMENDMENT_STATUS.DRAFT
    ? openAmendment
    : null;
  const amendmentHint = buildAmendmentPaneHint({
    workOrderId,
    showOpenHint,
    openDraft,
    canEditDraft: canUpdateAmendment,
  });

  const hasSelectedRequirements = selectedRequirementKeys.size > 0;

  const handleToggleRequirementKey = useCallback((rowKey, checked) => {
    setSelectedRequirementKeys((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(rowKey);
      } else {
        next.delete(rowKey);
      }
      return next;
    });
  }, []);

  const handleToggleAllPurchasable = useCallback((checked) => {
    const purchasableKeys = materialRequirements
      .map((row, index) => ({ row, key: resolveRequirementRowKey(row, index) }))
      .filter(({ row }) => hasRequirementShortage(row))
      .map(({ key }) => key);

    setSelectedRequirementKeys((prev) => {
      const next = new Set(prev);
      purchasableKeys.forEach((key) => {
        if (checked) {
          next.add(key);
        } else {
          next.delete(key);
        }
      });
      return next;
    });
  }, [materialRequirements]);

  const selectedRequirementRows = useMemo(
    () => materialRequirements.filter((row, index) => (
      selectedRequirementKeys.has(resolveRequirementRowKey(row, index))
    )),
    [materialRequirements, selectedRequirementKeys],
  );

  function handleCreatePurchase() {
    if (selectedRequirementRows.length === 0) {
      return;
    }
    navigate(buildPurchaseOrderCreateUrlFromRequirements(selectedRequirementRows));
  }

  const materialRequirementsActions = (
    <Button
      type="button"
      size="sm"
      disabled={!hasSelectedRequirements}
      className={cn(!hasSelectedRequirements && 'opacity-50')}
      onClick={handleCreatePurchase}
    >
      <span className="inline-flex items-center gap-0.5">
        {hasSelectedRequirements
          ? `新建採購 (${selectedRequirementRows.length})`
          : '新建採購'}
        {!hasSelectedRequirements ? (
          <ActionHintIcon
            tip={PURCHASE_REQUIREMENT_HINT}
            ariaLabel="新建採購說明"
          />
        ) : null}
      </span>
    </Button>
  );

  return (
    <TabbedDetailCard value={activeTab} onValueChange={handleDetailTabChange}>
      <TabbedDetailCard.List>
        <TabbedDetailCard.Trigger value="materialRequirements">用料需求</TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="bomSelections">替代料選擇</TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="expenses">費用</TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="snLabels">SN 標籤</TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="repairs">維修單</TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="stockIssues" disabled={fulfillmentTabsDisabled}>
          <div className="inline-flex items-center gap-0.5">
            出庫歷史
            <DraftTabTip
              label="出庫歷史"
              className={cn(!fulfillmentTabsDisabled && 'hidden')}
            />
          </div>
        </TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="stockReceipts" disabled={fulfillmentTabsDisabled}>
          <div className="inline-flex items-center gap-0.5">
            入庫歷史
            <DraftTabTip
              label="入庫歷史"
              className={cn(!fulfillmentTabsDisabled && 'hidden')}
            />
          </div>
        </TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="amendments" disabled={fulfillmentTabsDisabled}>
          <div className="inline-flex items-center gap-0.5">
            異動單
            <DraftTabTip
              label="異動單"
              className={cn(!fulfillmentTabsDisabled && 'hidden')}
            />
          </div>
        </TabbedDetailCard.Trigger>
        <TabbedDetailCard.Trigger value="statusHistories" disabled>
          狀態歷程
        </TabbedDetailCard.Trigger>
      </TabbedDetailCard.List>

      <TabbedDetailCard.Pane
        value="materialRequirements"
        title="用料需求"
        actions={materialRequirementsActions}
      >
        <WorkOrderMaterialRequirementsTable
          rows={materialRequirements}
          selectedKeys={selectedRequirementKeys}
          onToggleKey={handleToggleRequirementKey}
          onToggleAllPurchasable={handleToggleAllPurchasable}
          workOrderId={workOrderId}
        />
      </TabbedDetailCard.Pane>

      <TabbedDetailCard.Pane value="bomSelections" title="替代料選擇">
        <WorkOrderBomSelectionsTable
          rows={detail?.bomMtrSelections ?? detail?.bomMaterialSelections ?? []}
        />
      </TabbedDetailCard.Pane>

      <TabbedDetailCard.Pane value="expenses" title="費用">
        <WorkOrderExpensesTable rows={detail?.expenses ?? []} />
      </TabbedDetailCard.Pane>

      <TabbedDetailCard.Pane value="snLabels" title="預計 SN 標籤">
        <WorkOrderSnLabelPanel
          key={workOrderId}
          workOrderId={workOrderId}
          serialAllocation={detail?.serialAllocation ?? null}
        />
      </TabbedDetailCard.Pane>

      <TabbedDetailCard.Pane value="repairs" title="維修單">
        <WorkOrderRepairsPanel
          workOrderId={workOrderId}
          workOrderStatus={detail?.status}
          onChanged={onRefetch}
        />
      </TabbedDetailCard.Pane>

      <TabbedDetailCard.Pane
        value="stockIssues"
        title="出庫歷史"
        actions={canCreateWorkOrder
          ? renderStockDocAction(
            workOrderId,
            ROUTES.workOrderOutboundCreate(workOrderId),
            '新增出庫',
            canOperateStockDocs,
          )
          : null}
      >
        <WorkOrderRelatedDocsTable
          rows={detail?.stockIssues ?? []}
          variant="stockIssue"
          workOrderId={workOrderId}
        />
      </TabbedDetailCard.Pane>

      <TabbedDetailCard.Pane
        value="stockReceipts"
        title="入庫歷史"
        actions={canCreateWorkOrder
          ? renderStockDocAction(
            workOrderId,
            ROUTES.workOrderInboundCreate(workOrderId),
            '新增入庫',
            canOperateStockDocs,
          )
          : null}
      >
        <WorkOrderRelatedDocsTable
          rows={detail?.stockReceipts ?? []}
          variant="stockReceipt"
          workOrderId={workOrderId}
        />
      </TabbedDetailCard.Pane>

      <TabbedDetailCard.Pane
        value="amendments"
        title="異動單"
        hint={amendmentHint}
        actions={canCreateAmendmentPerm
          ? (canCreateAmendment && workOrderId ? (
            <Button asChild size="sm">
              <Link to={ROUTES.workOrderAmendmentCreate(workOrderId)}>
                新建異動單
              </Link>
            </Button>
          ) : (
            <Button type="button" size="sm" disabled>
              <span className="inline-flex items-center gap-0.5">
                新建異動單
                <ActionHintIcon
                  tip={
                    detail?.status === WORK_ORDER_STATUS.PROCESSING
                      ? OPEN_AMENDMENT_HINT
                      : AMENDMENT_NOT_PROCESSING_HINT
                  }
                  ariaLabel="新建異動單說明"
                />
              </span>
            </Button>
          ))
          : null}
      >
        <WorkOrderAmendmentsPanel detail={detail} onRefetch={onRefetch} />
      </TabbedDetailCard.Pane>
    </TabbedDetailCard>
  );
};

WorkOrderDetailSection.propTypes = {
  detail: PropTypes.shape({
    id: PropTypes.string,
    workOrderNo: PropTypes.string,
    status: PropTypes.string,
    mtrRequirements: PropTypes.arrayOf(PropTypes.object),
    materialRequirements: PropTypes.arrayOf(PropTypes.object),
    bomMtrSelections: PropTypes.arrayOf(PropTypes.object),
    bomMaterialSelections: PropTypes.arrayOf(PropTypes.object),
    stockIssues: PropTypes.arrayOf(PropTypes.object),
    stockReceipts: PropTypes.arrayOf(PropTypes.object),
    amendments: PropTypes.arrayOf(PropTypes.object),
    expenses: PropTypes.arrayOf(PropTypes.object),
    serialAllocation: PropTypes.object,
  }).isRequired,
  onRefetch: PropTypes.func,
};

export default WorkOrderDetailSection;
